import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Button } from 'react-bootstrap';
import { BiNetworkChart } from 'react-icons/bi';
import { FiRefreshCw } from 'react-icons/fi';
import PageHeader from 'Utils/PageHeader';
import FetchContainer from 'Utils/FetchContainer';
import AppContext from 'Utils/AppContext';
import { READ_ONLY_TITLE } from 'Auth/permissions';
import { startProfileJob } from 'Metadata/profileApi';
import { getOverview } from 'Metadata/metaAnalysisApi';
import { getGraphSummary, isGraphUnavailable, isGraphCapable, listDataGraphs } from './dataGraphApi';
import SourceDetailPanel from './SourceDetailPanel';
import { sourcePath } from './paths';

const fmt = (n) => (n == null ? '—' : Number(n).toLocaleString());
const when = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { month: 'numeric', day: 'numeric' }) : '—');
const engineOf = (c) => (c.engine || c.type || '').toString();

/**
 * dataSourceId → the commercial schema its latest profile matched, from the ONE
 * meta-analysis overview call. Every member of an identity cluster inherits the cluster's
 * headline match: that is what the match means — these sources share a fingerprint.
 */
export const indexFingerprints = (identities) => {
  const out = {};
  (identities || []).forEach((identity) => {
    const cm = identity.commercialMatch;
    const product = cm ? [cm.vendorName, cm.productName].filter(Boolean).join(' ') : null;
    (identity.dataSources || []).forEach((m) => {
      if (!m || m.dataSourceId == null) return;
      out[String(m.dataSourceId)] = {
        product: product || null,
        confidence: cm && cm.confidence != null ? Number(cm.confidence) : null,
        confidenceLabel: (cm && cm.confidenceLabel) || null,
      };
    });
  });
  return out;
};

// Sortable columns: key → value accessor over a { connection, summary, fingerprint, graphs } row.
// Sources without a graph sort last on numeric/date columns regardless of direction.
const SORT_COLUMNS = {
  name: {
    label: 'Source',
    tip: 'The data source as it is named under Data Sources.',
    value: (r) => r.connection.name || '',
  },
  engine: {
    label: 'Engine',
    tip: 'Database engine this source runs on.',
    value: (r) => engineOf(r.connection),
  },
  fingerprint: {
    label: 'Fingerprint',
    tip: 'The commercial application whose schema this source matched when it was fingerprinted '
      + '(vendor + product, with match confidence). "Not matched" means no known product '
      + 'fingerprint cleared the threshold — a custom or in-house schema.',
    value: (r) => (r.fingerprint && r.fingerprint.product) || '',
  },
  tables: {
    label: 'Tables',
    tip: 'Tables captured in the latest fingerprint snapshot — the nodes of this graph.',
    value: (r) => (r.summary ? Number(r.summary.tableCount) || 0 : null),
    numeric: true,
  },
  declared: {
    label: 'Declared',
    tip: 'Foreign keys the source itself declares, read straight from its catalog. These are '
      + 'the edges subsetting walks by default.',
    value: (r) => (r.summary ? Number(r.summary.declaredEdgeCount) || 0 : null),
    numeric: true,
  },
  graphs: {
    label: 'Graphs',
    tip: 'Named data graphs set up for this source. Each is one way a test data subset pulls it: '
      + 'a key, a hop limit and its own manual links. A subset cannot use a source with none.',
    value: (r) => (r.graphs ? r.graphs.length : 0),
    numeric: true,
  },
  synced: {
    label: 'Last fingerprinted',
    tip: 'When the fingerprint snapshot behind this graph was taken. The graph always reflects the '
      + 'latest snapshot — "Re-sync schema" runs a new fingerprint to pick up schema changes.',
    value: (r) => (r.summary && r.summary.snapshotTakenAt ? Date.parse(r.summary.snapshotTakenAt) : null),
    numeric: true,
  },
};

export const sortRows = (rows, sortKey, dir) => {
  const col = SORT_COLUMNS[sortKey] || SORT_COLUMNS.name;
  const sign = dir === 'desc' ? -1 : 1;
  return [...rows].sort((a, b) => {
    const va = col.value(a);
    const vb = col.value(b);
    if (va == null && vb == null) return 0;
    if (va == null) return 1;           // no graph → last
    if (vb == null) return -1;
    if (col.numeric) return sign * (va - vb);
    return sign * String(va).localeCompare(String(vb), undefined, { sensitivity: 'base' });
  });
};

export const filterRows = (rows, q) => {
  const needle = String(q || '').trim().toLowerCase();
  if (!needle) return rows;
  return rows.filter((r) => (r.connection.name || '').toLowerCase().includes(needle)
    || engineOf(r.connection).toLowerCase().includes(needle)
    || String((r.fingerprint && r.fingerprint.product) || '').toLowerCase().includes(needle));
};

// "Data graph sources": every source a subset can pull from (relational, MongoDB, Salesforce) with
// the header counts of its captured structure (declared keys from the source) and how many named
// data graphs it has, or a "run Profile" nudge when no snapshot exists yet. One summary call per
// source, in parallel, plus one list of every graph. Clicking a row expands it in place to the
// source's identifying detail (see SourceDetailPanel); Review opens its data graphs.
const GraphList = () => {
  const history = useHistory();
  const { connections, isReadOnly } = useContext(AppContext);
  const eligible = useMemo(() => (connections || []).filter(isGraphCapable), [connections]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [started, setStarted] = useState({});
  const [error, setError] = useState(null);
  const [query, setQuery] = useState('');
  const [sortKey, setSortKey] = useState('name');
  const [sortDir, setSortDir] = useState('asc');
  const [expanded, setExpanded] = useState(null);
  // null while the overview is in flight, {} once it has answered (or failed — the column
  // degrades to "unknown" rather than taking the page down with it).
  const [fingerprints, setFingerprints] = useState(null);
  const [graphsBySource, setGraphsBySource] = useState({});

  useEffect(() => {
    let alive = true;
    listDataGraphs()
      .then((all) => {
        if (!alive) return;
        const by = {};
        all.forEach((g) => { (by[String(g.dataSourceId)] = by[String(g.dataSourceId)] || []).push(g); });
        setGraphsBySource(by);
      })
      .catch(() => { /* the column reads 0; the source page lists them */ });
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      setLoading(true);
      const settled = await Promise.allSettled(eligible.map((c) => getGraphSummary(c.id)));
      if (!alive) return;
      setRows(eligible.map((c, i) => {
        const r = settled[i];
        if (r.status === 'fulfilled') return { connection: c, summary: r.value, error: null };
        return { connection: c, summary: null, error: isGraphUnavailable(r.reason) ? null : (r.reason?.message || 'failed') };
      }));
      setLoading(false);
    };
    load();
    return () => { alive = false; };
  }, [eligible]);

  // Fingerprints ride in on their own, after the table is already usable.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const identities = await getOverview();
        if (alive) setFingerprints(indexFingerprints(identities));
      } catch (e) {
        if (alive) setFingerprints({});
      }
    })();
    return () => { alive = false; };
  }, []);

  const resync = async (c) => {
    try {
      const job = await startProfileJob(Number(c.id));
      setStarted((prev) => ({ ...prev, [c.id]: job?.id || true }));
    } catch (e) {
      setError(e?.message || 'Could not start Fingerprint');
    }
  };

  const withGraph = rows.filter((r) => r.summary).length;
  const withFingerprints = useMemo(
    () => rows.map((r) => ({
      ...r,
      fingerprint: (fingerprints && fingerprints[String(r.connection.id)]) || null,
      graphs: graphsBySource[String(r.connection.id)] || [],
    })),
    [rows, fingerprints, graphsBySource],
  );
  const visible = useMemo(
    () => sortRows(filterRows(withFingerprints, query), sortKey, sortDir),
    [withFingerprints, query, sortKey, sortDir],
  );

  const toggleSort = (key) => {
    if (key === sortKey) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir(SORT_COLUMNS[key].numeric ? 'desc' : 'asc');
    }
  };
  const Head = ({ colKey, numeric }) => {
    const col = SORT_COLUMNS[colKey];
    const sorted = sortKey === colKey
      ? `Sorted ${sortDir === 'asc' ? 'ascending' : 'descending'} — click to flip`
      : 'Click to sort';
    return (
      <button type='button' className={`dg-sort${numeric ? ' dg-num' : ''}${sortKey === colKey ? ' dg-sort--on' : ''}`}
        onClick={() => toggleSort(colKey)} data-testid={`dg-sort-${colKey}`}
        title={`${col.tip}\n\n${sorted}`}>
        {col.label}{sortKey === colKey ? (sortDir === 'asc' ? ' ▲' : ' ▼') : ''}
      </button>
    );
  };

  // Three distinct states, because "blank" would hide the difference: still loading, profiled
  // but matching no known product, and never profiled at all (nothing to match against).
  const fingerprintCell = (fp, profiled) => {
    if (fingerprints == null) return <span className='dg-muted'>…</span>;
    if (!fp || !fp.product) {
      return <span className='dg-muted' title={profiled
        ? 'This schema matched no product in the schema library — a custom or in-house schema.'
        : 'Run Fingerprint to identify this source.'}>{profiled ? 'Not matched' : '—'}</span>;
    }
    return (
      <span title={fp.confidence != null ? `${Math.round(fp.confidence * 100)}% schema match` : undefined}>
        {fp.product}
        {fp.confidence != null && <span className='dg-muted'> · {Math.round(fp.confidence * 100)}%</span>}
      </span>
    );
  };

  return (
    <FetchContainer fetchingData={loading} absolute={true} error={error}>
      <div className='dg-page'>
        <PageHeader
          icon={BiNetworkChart}
          eyebrow='Advanced Settings'
          header='Data Graph'
          tooltip='How the tables of each source connect: the edges test data subsetting walks'
          help='Set up the named data graphs a test data subset pulls each source through: the key, the hop limit and the manual links. Customers only pick one per subset source.'
          meta={<span>{withGraph} of {eligible.length} sources fingerprinted · {Object.keys(graphsBySource).length} with a data graph</span>}
        />
        {eligible.length === 0 && (
          <div className='dg-empty'>No relational, MongoDB or Salesforce sources yet. Add a data source and run Fingerprint to capture its structure.</div>
        )}
        {eligible.length > 0 && (
          <input className='dg-search' type='search' placeholder='Search sources by name, engine or fingerprint…' value={query}
            onChange={(e) => setQuery(e.target.value)} data-testid='dg-search' aria-label='Search sources' />
        )}
        {eligible.length > 0 && visible.length === 0 && (
          <div className='dg-empty'>No sources match “{query}”.</div>
        )}
        {visible.length > 0 && (
          <div className='dg-table' role='table' aria-label='Data graph sources'>
            <div className='dg-table__head' role='row'>
              <Head colKey='name' /><Head colKey='engine' /><Head colKey='fingerprint' /><Head colKey='tables' numeric />
              <Head colKey='declared' numeric /><Head colKey='graphs' numeric /><Head colKey='synced' numeric /><span />
            </div>
            {visible.map(({ connection: c, summary: s, error: err, fingerprint: fp, graphs }) => {
              const open = String(expanded) === String(c.id);
              return (
                <React.Fragment key={c.id}>
                  <div className={`dg-table__row dg-table__row--clickable${open ? ' dg-table__row--open' : ''}`} role='row'
                    data-testid={`dg-row-${c.id}`} aria-expanded={open}
                    onClick={() => setExpanded(open ? null : c.id)}>
                    <span className='dg-name'>
                      <span className='dg-caret'>{open ? '▾' : '▸'}</span>{c.name}
                    </span>
                    <span className='dg-muted'>{engineOf(c).toUpperCase()}</span>
                    <span className='dg-clip'>{fingerprintCell(fp, Boolean(s))}</span>
                    <span className='dg-num'>{s ? fmt(s.tableCount) : '—'}</span>
                    <span className='dg-num'>{s ? fmt(s.declaredEdgeCount) : '—'}</span>
                    <span className='dg-num' data-testid={`dg-graphs-${c.id}`}
                      title={graphs.length ? graphs.map((g) => g.name).join(', ') : 'No data graph: subsets cannot use this source yet'}>
                      {fmt(graphs.length)}
                    </span>
                    <span className='dg-num dg-muted'>
                      {s ? when(s.snapshotTakenAt) : (err ? `Unavailable: ${err}` : 'Not fingerprinted')}
                    </span>
                    <span className='dg-actions' onClick={(e) => e.stopPropagation()}>
                      {s && (
                        <Button size='sm' variant='outline-primary'
                          onClick={() => history.push(sourcePath(c.id))} data-testid={`dg-open-${c.id}`}
                          title='The data graphs of this source: set up the key, hop limit and the links the schema did not declare'>
                          Review
                        </Button>
                      )}
                      {started[c.id] && <span className='dg-muted'>Fingerprint started</span>}
                      {/* With a graph the re-sync is a maintenance action, so it shrinks to the
                          recycle glyph next to Review. Without one it is the whole point of the
                          row, and stays a labelled button. */}
                      {!started[c.id] && s && (
                        <Button size='sm' variant='outline-secondary' className='dg-iconbtn'
                          onClick={() => resync(c)} data-testid={`dg-resync-${c.id}`}
                          disabled={!!isReadOnly}
                          aria-label={`Re-sync schema for ${c.name}`}
                          title={isReadOnly ? READ_ONLY_TITLE : 'Re-sync schema — runs a new Fingerprint (a Describe for a Salesforce org) so the graph picks up schema changes'}>
                          <FiRefreshCw />
                        </Button>
                      )}
                      {!started[c.id] && !s && (
                        <Button size='sm' variant='primary' onClick={() => resync(c)}
                          disabled={!!isReadOnly} title={isReadOnly ? READ_ONLY_TITLE : undefined}
                          data-testid={`dg-resync-${c.id}`}>
                          Run Fingerprint
                        </Button>
                      )}
                    </span>
                  </div>
                  {open && (
                    <SourceDetailPanel dataSourceId={c.id} connection={c} summary={s} fingerprint={fp}
                      onReview={() => history.push(sourcePath(c.id))} />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        )}
      </div>
    </FetchContainer>
  );
};

export default GraphList;
